import type { SeacloudClient } from './client.js';
import type { TaskResult, PollingOptions, TaskStatus } from './types.js';
import { SeacloudError } from './types.js';

/**
 * 默认轮询选项
 */
const DEFAULT_POLLING_OPTIONS: Required<PollingOptions> = {
  intervalMs: 3000,
  maxAttempts: 120,
  onProgress: () => {},
};

/**
 * 检查任务是否已完成
 */
function isTaskComplete(status: TaskStatus): boolean {
  return status === 'completed' || status === 'failed';
}

/**
 * 等待一段时间
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * 判断错误是否为网络错误
 */
function isNetworkError(error: any): boolean {
  // SeacloudError with network-related status codes
  if (error instanceof SeacloudError) {
    const statusCode = error.statusCode;
    if (!statusCode) return false;

    // 网络相关的状态码
    const networkStatusCodes = [
      408, // Request Timeout
      500, // Internal Server Error
      502, // Bad Gateway
      503, // Service Unavailable
      504, // Gateway Timeout
    ];

    return networkStatusCodes.includes(statusCode);
  }

  // Network errors (Node.js & Browser)
  if (error instanceof Error) {
    const errorCode = (error as any).code;
    const networkErrorCodes = [
      'ETIMEDOUT',
      'ECONNRESET',
      'ECONNREFUSED',
      'ENOTFOUND',
      'EAI_AGAIN',
      'ENETUNREACH',
      'EHOSTUNREACH',
    ];

    if (errorCode && networkErrorCodes.includes(errorCode)) {
      return true;
    }
  }

  return false;
}

/**
 * 轮询任务直到完成或失败
 * @param client SeaCloud 客户端实例
 * @param endpoint API 端点路径
 * @param taskId 任务 ID
 * @param options 轮询选项
 * @returns 最终的任务结果
 * @throws SeacloudError 如果任务失败或超时
 */
export async function pollTaskUntilComplete(
  client: SeacloudClient,
  endpoint: string,
  taskId: string,
  options: PollingOptions = {},
): Promise<TaskResult> {
  const opts = { ...DEFAULT_POLLING_OPTIONS, ...options };
  let consecutiveNetworkErrors = 0;
  const MAX_CONSECUTIVE_NETWORK_ERRORS = 3;

  for (let attempt = 1; attempt <= opts.maxAttempts; attempt++) {
    try {
      // 查询任务状态
      const result = await client.getTaskStatus(endpoint, taskId);

      // 成功请求，重置连续网络错误计数
      consecutiveNetworkErrors = 0;

      // 调用进度回调
      opts.onProgress(attempt, result.status);

      // 检查是否完成
      if (isTaskComplete(result.status)) {
        if (result.status === 'failed') {
          const errorMsg = result.error?.message || 'Task failed without error message';
          throw new SeacloudError(
            `Task failed: ${errorMsg}`,
            undefined,
            result.error,
          );
        }
        return result;
      }

      // 如果还未完成，等待后继续轮询
      if (attempt < opts.maxAttempts) {
        await sleep(opts.intervalMs);
      }
    } catch (error) {
      // 判断是否为网络错误
      if (isNetworkError(error)) {
        consecutiveNetworkErrors++;

        if (consecutiveNetworkErrors >= MAX_CONSECUTIVE_NETWORK_ERRORS) {
          throw new SeacloudError(
            `Task polling failed: encountered ${MAX_CONSECUTIVE_NETWORK_ERRORS} consecutive network errors. Last error: ${(error as Error).message}`,
            (error as SeacloudError).statusCode,
            error,
          );
        }

        // 网络错误但未达到上限，等待后继续
        if (attempt < opts.maxAttempts) {
          await sleep(opts.intervalMs);
        }
      } else {
        // 非网络错误（认证错误、业务错误等），直接抛出
        throw error;
      }
    }
  }

  // 达到最大尝试次数
  throw new SeacloudError(
    `Task polling timeout after ${opts.maxAttempts} attempts (${(opts.maxAttempts * opts.intervalMs) / 1000}s)`,
  );
}

/**
 * 创建任务并等待完成
 * @param client SeaCloud 客户端实例
 * @param endpoint API 端点路径
 * @param body 请求体
 * @param options 轮询选项
 * @returns 最终的任务结果
 */
export async function createAndWaitTask(
  client: SeacloudClient,
  endpoint: string,
  body: Record<string, any>,
  options: PollingOptions = {},
): Promise<TaskResult> {
  // 创建任务
  const task = await client.createTask(endpoint, body);

  // 检查是否立即失败
  if (task.status === 'failed') {
    const errorMsg = task.error?.message || 'Task failed without error message';
    throw new SeacloudError(
      `Task failed: ${errorMsg}`,
      undefined,
      task.error,
    );
  }

  // 总是进行轮询，因为创建任务的响应可能不包含完整的结果
  // 即使status是completed，也需要查询任务状态来获取output
  return pollTaskUntilComplete(client, endpoint, task.id, options);
}
